import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { formatPrice } from '../lib/utils';
import { toast } from 'sonner';
import { 
  ChevronLeft, 
  Zap, 
  AlertCircle,
  Wallet,
  CreditCard,
  QrCode,
  Building2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PAYMENT_METHODS = [
  { id: 'qris', name: 'QRIS', icon: QrCode, description: 'GoPay, OVO, Dana, dll' },
  { id: 'va_bca', name: 'Virtual Account BCA', icon: Building2, description: 'Transfer via BCA' },
  { id: 'va_bni', name: 'Virtual Account BNI', icon: Building2, description: 'Transfer via BNI' },
  { id: 'va_mandiri', name: 'Virtual Account Mandiri', icon: Building2, description: 'Transfer via Mandiri' },
];

export default function ProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDenom, setSelectedDenom] = useState(null);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [userId, setUserId] = useState('');
  const [serverId, setServerId] = useState('');
  const [email, setEmail] = useState(user?.email || '');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchProduct();
  }, [slug]);

  useEffect(() => {
    if (user?.email) {
      setEmail(user.email);
    }
  }, [user]);

  const fetchProduct = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/products/${slug}`);
      setProduct(response.data.product);
    } catch (error) {
      console.error('Failed to fetch product:', error);
      toast.error('Produk tidak ditemukan');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const getPrice = (denom) => {
    if (user?.role === 'reseller') {
      return denom.reseller_price;
    }
    return denom.price;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedDenom) {
      toast.error('Pilih nominal terlebih dahulu');
      return;
    }

    if (!selectedPayment) {
      toast.error('Pilih metode pembayaran');
      return;
    }

    if (product.input_fields.includes('user_id') && !userId) {
      toast.error('Masukkan User ID');
      return;
    }

    if (product.input_fields.includes('server_id') && !serverId) {
      toast.error('Masukkan Server ID');
      return;
    }

    if (!email) {
      toast.error('Masukkan email');
      return;
    }

    setSubmitting(true);

    try {
      const orderData = {
        product_id: product.id,
        denomination_id: selectedDenom.id,
        game_user_id: userId,
        game_server_id: serverId || null,
        email: email,
        payment_method: selectedPayment.id
      };

      const endpoint = user 
        ? `${API_URL}/api/orders/authenticated`
        : `${API_URL}/api/orders/guest`;

      const headers = user 
        ? { Authorization: `Bearer ${token}` }
        : {};

      const response = await axios.post(endpoint, orderData, { headers });
      const order = response.data.order;

      toast.success('Order berhasil dibuat!');
      navigate(`/checkout/${order.id}`, { state: { order } });
    } catch (error) {
      console.error('Failed to create order:', error);
      toast.error(error.response?.data?.detail || 'Gagal membuat order');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-16 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!product) {
    return null;
  }

  return (
    <div className="min-h-screen pt-16 cyber-grid">
      {/* Banner */}
      <div className="relative h-48 md:h-64 overflow-hidden border-b border-border/50">
        <img
          src={product.banner_image || product.image}
          alt={product.name}
          className="w-full h-full object-cover opacity-70"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/40 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-4 md:p-8 max-w-7xl mx-auto">
          <Button
            variant="ghost"
            size="sm"
            className="text-white hover:text-primary mb-4 font-mono text-xs"
            onClick={() => navigate('/')}
            data-testid="back-btn"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            [ KEMBALI ]
          </Button>
          <div className="flex items-center gap-4">
            <img
              src={product.image}
              alt={product.name}
              className="w-16 h-16 md:w-20 md:h-20 rounded-xl object-cover border-2 border-primary/50 neon-green"
            />
            <div>
              <h1 className="font-bold text-2xl md:text-3xl text-white uppercase tracking-wider">
                {product.name}
              </h1>
              <p className="text-primary font-mono text-xs uppercase tracking-widest mt-1">NODE // {product.category}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left - Form */}
          <div className="lg:col-span-2 space-y-8">
            {/* Instructions */}
            {product.instructions && (
              <div className="bg-secondary/5 border border-secondary/30 rounded-xl p-4 relative overflow-hidden">
                <div className="absolute top-0 left-0 w-2 h-2 border-t border-l border-secondary" />
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-secondary flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-gray-300 font-mono">{product.instructions}</p>
                </div>
              </div>
            )}

            {/* ID Input */}
            {product.input_fields.length > 0 && (
              <div className="bg-card/80 backdrop-blur-sm rounded-xl p-6 border border-border cyber-card relative overflow-hidden">
                <div className="absolute top-0 left-0 w-6 h-6 border-t-2 border-l-2 border-primary/50 rounded-tl-xl" />
                <div className="absolute bottom-0 right-0 w-6 h-6 border-b-2 border-r-2 border-secondary/50 rounded-br-xl" />
                <h2 className="font-mono font-bold text-sm text-white uppercase mb-4 tracking-wider">
                  [ 01 · DATA AKUN TARGET ]
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {product.input_fields.includes('user_id') && (
                    <div className="space-y-2">
                      <Label htmlFor="userId" className="font-mono text-xs text-muted-foreground uppercase tracking-widest">▸ User ID</Label>
                      <Input
                        id="userId"
                        type="text"
                        placeholder="Masukkan User ID"
                        className="bg-black/50 border-border text-white font-mono focus:border-primary focus:ring-1 focus:ring-primary/30"
                        value={userId}
                        onChange={(e) => setUserId(e.target.value)}
                        data-testid="user-id-input"
                      />
                    </div>
                  )}
                  {product.input_fields.includes('server_id') && (
                    <div className="space-y-2">
                      <Label htmlFor="serverId" className="font-mono text-xs text-muted-foreground uppercase tracking-widest">▸ Server ID / Zone ID</Label>
                      <Input
                        id="serverId"
                        type="text"
                        placeholder="Masukkan Server ID"
                        className="bg-black/50 border-border text-white font-mono focus:border-primary focus:ring-1 focus:ring-primary/30"
                        value={serverId}
                        onChange={(e) => setServerId(e.target.value)}
                        data-testid="server-id-input"
                      />
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Denominations */}
            <div className="bg-card/80 backdrop-blur-sm rounded-xl p-6 border border-border cyber-card relative overflow-hidden">
              <div className="absolute top-0 left-0 w-6 h-6 border-t-2 border-l-2 border-primary/50 rounded-tl-xl" />
              <div className="absolute bottom-0 right-0 w-6 h-6 border-b-2 border-r-2 border-secondary/50 rounded-br-xl" />
              <h2 className="font-mono font-bold text-sm text-white uppercase mb-4 tracking-wider">
                [ {product.input_fields.length > 0 ? '02' : '01'} · SELEKSI NOMINAL ]
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {product.denominations.map((denom) => (
                  <button
                    key={denom.id}
                    className={`denom-card p-4 rounded-xl text-left border border-border/50 transition-all ${
                      selectedDenom?.id === denom.id ? 'selected border-primary bg-primary/10' : 'bg-black/30 hover:border-primary/50'
                    }`}
                    onClick={() => setSelectedDenom(denom)}
                    data-testid={`denom-${denom.id}`}
                  >
                    <p className="font-mono font-bold text-white text-xs uppercase tracking-wider">{denom.name}</p>
                    <p className="font-mono text-sm text-primary mt-1 font-bold">
                      {formatPrice(getPrice(denom))}
                    </p>
                    {user?.role === 'reseller' && denom.price !== denom.reseller_price && (
                      <p className="text-[10px] font-mono text-muted-foreground line-through">
                        {formatPrice(denom.price)}
                      </p>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Payment Methods */}
            <div className="bg-card/80 backdrop-blur-sm rounded-xl p-6 border border-border cyber-card relative overflow-hidden">
              <div className="absolute top-0 left-0 w-6 h-6 border-t-2 border-l-2 border-primary/50 rounded-tl-xl" />
              <div className="absolute bottom-0 right-0 w-6 h-6 border-b-2 border-r-2 border-secondary/50 rounded-br-xl" />
              <h2 className="font-mono font-bold text-sm text-white uppercase mb-4 tracking-wider">
                [ {product.input_fields.length > 0 ? '03' : '02'} · JALUR TRANSMISI ]
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {PAYMENT_METHODS.map((method) => {
                  const Icon = method.icon;
                  return (
                    <button
                      key={method.id}
                      className={`payment-card p-4 rounded-xl flex items-center gap-3 border border-border/50 transition-all ${
                        selectedPayment?.id === method.id ? 'selected border-secondary bg-secondary/10' : 'bg-black/30 hover:border-secondary/50'
                      }`}
                      onClick={() => setSelectedPayment(method)}
                      data-testid={`payment-${method.id}`}
                    >
                      <div className="w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center flex-shrink-0 border border-border/40">
                        <Icon className="w-5 h-5 text-secondary" />
                      </div>
                      <div className="text-left font-mono">
                        <p className="font-bold text-white text-xs uppercase tracking-wide">{method.name}</p>
                        <p className="text-[10px] text-muted-foreground">{method.description}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Email */}
            <div className="bg-card/80 backdrop-blur-sm rounded-xl p-6 border border-border cyber-card relative overflow-hidden">
              <div className="absolute top-0 left-0 w-6 h-6 border-t-2 border-l-2 border-primary/50 rounded-tl-xl" />
              <div className="absolute bottom-0 right-0 w-6 h-6 border-b-2 border-r-2 border-secondary/50 rounded-br-xl" />
              <h2 className="font-mono font-bold text-sm text-white uppercase mb-4 tracking-wider">
                [ {product.input_fields.length > 0 ? '04' : '03'} · EMAIL VERIFIKASI ]
              </h2>
              <div className="space-y-2">
                <Label htmlFor="email" className="font-mono text-xs text-muted-foreground uppercase tracking-widest">▸ Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="Masukkan email"
                  className="bg-black/50 border-border text-white font-mono focus:border-primary focus:ring-1 focus:ring-primary/30"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="email-input"
                />
                <p className="text-[10px] font-mono text-muted-foreground">
                  Bukti log transaksi orbital akan dikirim ke alamat email ini
                </p>
              </div>
            </div>
          </div>

          {/* Right - Summary */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 bg-card/80 backdrop-blur-sm rounded-xl p-6 border border-border cyber-card relative overflow-hidden">
              <div className="absolute top-0 left-0 w-6 h-6 border-t-2 border-l-2 border-primary/50 rounded-tl-xl" />
              <div className="absolute bottom-0 right-0 w-6 h-6 border-b-2 border-r-2 border-secondary/50 rounded-br-xl" />
              <h2 className="font-mono font-bold text-sm text-white uppercase mb-4 tracking-wider">
                [ MANIFEST PESANAN ]
              </h2>

              <div className="space-y-4 mb-6">
                <div className="flex items-center gap-3 border-b border-border/40 pb-3">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-12 h-12 rounded-lg object-cover border border-border"
                  />
                  <div>
                    <p className="font-bold text-white text-xs uppercase tracking-wide">{product.name}</p>
                    {selectedDenom && (
                      <p className="text-xs text-primary font-mono">{selectedDenom.name}</p>
                    )}
                  </div>
                </div>

                {(userId || serverId) && (
                  <div className="bg-black/40 rounded-lg p-3 font-mono text-xs border border-border/50">
                    {userId && (
                      <p className="text-gray-300">
                        TARGET ID: <span className="text-white font-bold">{userId}</span>
                      </p>
                    )}
                    {serverId && (
                      <p className="text-gray-300">
                        SERVER ID: <span className="text-white font-bold">{serverId}</span>
                      </p>
                    )}
                  </div>
                )}

                {selectedPayment && (
                  <div className="flex items-center gap-2 text-xs font-mono text-gray-300">
                    <Wallet className="w-4 h-4 text-secondary" />
                    CHANNEL: {selectedPayment.name.toUpperCase()}
                  </div>
                )}
              </div>

              <div className="border-t border-border pt-4 mb-6">
                <div className="flex justify-between items-center">
                  <span className="font-mono text-xs text-muted-foreground uppercase">TOTAL HARGA</span>
                  <span className="font-mono text-lg font-bold text-white text-glow-green">
                    {selectedDenom ? formatPrice(getPrice(selectedDenom)) : 'Rp 0'}
                  </span>
                </div>
              </div>

              <Button
                className="w-full bg-primary hover:bg-primary/80 text-black font-bold uppercase tracking-widest py-6 neon-green text-sm"
                onClick={handleSubmit}
                disabled={submitting || !selectedDenom || !selectedPayment}
                data-testid="order-btn"
              >
                {submitting ? (
                  <span className="flex items-center gap-2 font-mono">
                    <div className="animate-spin w-4 h-4 border-2 border-black border-t-transparent rounded-full" />
                    PROCESSING...
                  </span>
                ) : (
                  <span className="flex items-center gap-2 font-mono">
                    <Zap className="w-4 h-4" />
                    [ TRANSAKSI SEKARANG ]
                  </span>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
